import { useState, FormEvent } from 'react';

const repairServices = [
  { icon: 'ri-flashlight-fill', title: 'Fault Finding', desc: 'Expert diagnostics to identify exactly what\'s wrong with your system — fast.' },
  { icon: 'ri-tools-fill', title: 'System Repairs', desc: 'From inverter replacements to panel repairs. We fix it right, the first time.' },
  { icon: 'ri-calendar-line', title: 'Maintenance Plans', desc: 'Annual servicing plans to keep your system running at peak performance.' },
];

export default function RepairsSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [issue, setIssue] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !issue) { setError('Please fill in all required fields.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ name, email, phone, issue });
      await fetch('https://readdy.ai/api/form/d7426jr41deorl02el2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="repairs" className="bg-[#f7f8fa] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Left content */}
          <div>
            <p className="text-[#4CAF50] font-semibold text-sm uppercase tracking-widest mb-2">Support</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-[#1F2933] mb-4">
              Repairs, Servicing &amp; Ongoing Support
            </h2>
            <p className="text-gray-500 text-base mb-8 leading-relaxed">
              Already have a solar, battery or EV system? We service and repair all major brands — not just our own installs.
            </p>

            <div className="flex flex-col gap-5">
              {repairServices.map((svc) => (
                <div key={svc.title} className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#145A32]/10 flex-shrink-0">
                    <i className={`${svc.icon} text-[#145A32] text-xl`} />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-[#1F2933] text-base mb-1">{svc.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Booking form */}
          <div className="bg-white rounded-2xl p-6 md:p-8">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-fill text-[#7AC943] text-5xl" />
                </div>
                <h3 className="font-heading font-bold text-xl text-[#1F2933] mb-2">Booking Received!</h3>
                <p className="text-gray-500 text-sm">We&apos;ll contact you within 2 hours to confirm your repair booking.</p>
              </div>
            ) : (
              <>
                <h3 className="font-heading font-bold text-xl text-[#1F2933] mb-1">Book a Repair</h3>
                <p className="text-gray-500 text-sm mb-5">We&apos;ll respond within 2 hours.</p>
                <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your name *"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email address *"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors"
                  />
                  <select
                    name="issue"
                    value={issue}
                    onChange={(e) => setIssue(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors cursor-pointer"
                  >
                    <option value="">Issue type *</option>
                    <option value="Solar System Fault">Solar System Fault</option>
                    <option value="Battery Issue">Battery Issue</option>
                    <option value="EV Charger Problem">EV Charger Problem</option>
                    <option value="Annual Servicing">Annual Servicing</option>
                    <option value="Maintenance Plan">Maintenance Plan Enquiry</option>
                  </select>
                  {error && <p className="text-red-500 text-xs bg-red-50 rounded-lg px-3 py-2">{error}</p>}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-gradient-to-r from-[#4CAF50] to-[#7AC943] text-white font-bold py-4 rounded-xl text-base hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70"
                  >
                    {submitting ? 'Booking...' : 'Book a Repair →'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
